import { useState } from "react";
import { FileUp } from "lucide-react";
import { Button, Card, CardTitle, Select, cn } from "@clevis/shared-ui";
import { call, CallError } from "../lib/callFunction";
import { parseCsv } from "../lib/csv";
import type { InventoryRow } from "./InventoryList";

// Stock counts from another system: FieldPulse's inventory export, or any
// spreadsheet with a part number or name and a quantity. Clevis matches each
// row to a pricebook item (part number first, then exact name), shows what
// will change, and the import sets each count (one movement per item).

type Col = "sku" | "name" | "qty" | "bin" | "reorder";
const GUESS: Record<Col, RegExp> = {
  sku: /^(sku|part\s*(#|no\.?|number)?|item\s*(code|#|number)|product\s*code|upc|model)$/i,
  name: /^(name|item(\s*name)?|product(\s*name)?|description)$/i,
  qty: /^(qty|quantity|on\s*hand|qty\s*on\s*hand|in\s*stock|stock|count|available|quantity\s*on\s*hand)$/i,
  bin: /^(bin|location|shelf|warehouse\s*location)$/i,
  reorder: /^(reorder(\s*(level|point|at))?|min(imum)?(\s*qty)?|par(\s*level)?)$/i,
};
const LABEL: Record<Col, string> = { sku: "Part number", name: "Name", qty: "Quantity on hand", bin: "Bin (optional)", reorder: "Reorder level (optional)" };
const num = (s: string | undefined) => {
  const n = parseFloat((s ?? "").replace(/[, ]/g, ""));
  return Number.isFinite(n) ? n : null;
};

interface Match {
  line: number;
  item: InventoryRow | null;
  label: string;
  onHand: number;
  bin?: string;
  reorderLevel?: number | null;
}

export function StockImport({ tenantId, items, onDone }: { tenantId: string; items: InventoryRow[]; onDone: (msg: string) => void }) {
  const [rows, setRows] = useState<string[][] | null>(null);
  const [fileName, setFileName] = useState("");
  const [cols, setCols] = useState<Record<Col, number>>({ sku: -1, name: -1, qty: -1, bin: -1, reorder: -1 });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function load(f: File) {
    setError("");
    const parsed = parseCsv(await f.text());
    if (parsed.length < 2) return setError("That file has no rows under its header.");
    const header = parsed[0]!.map((h) => h.trim());
    const guess = Object.fromEntries((Object.keys(GUESS) as Col[]).map((c) => [c, header.findIndex((h) => GUESS[c].test(h))])) as Record<Col, number>;
    setCols(guess);
    setRows(parsed);
    setFileName(f.name);
  }

  const header = rows?.[0] ?? [];
  const bySku = new Map(items.filter((i) => i.sku?.trim()).map((i) => [i.sku.trim().toLowerCase(), i]));
  const byName = new Map(items.map((i) => [i.name.trim().toLowerCase(), i]));
  const matches: Match[] = [];
  if (rows && cols.qty >= 0 && (cols.sku >= 0 || cols.name >= 0)) {
    rows.slice(1).forEach((r, i) => {
      const onHand = num(r[cols.qty]);
      if (onHand === null) return;
      const sku = cols.sku >= 0 ? (r[cols.sku] ?? "").trim() : "";
      const name = cols.name >= 0 ? (r[cols.name] ?? "").trim() : "";
      const item = (sku && bySku.get(sku.toLowerCase())) || (name && byName.get(name.toLowerCase())) || null;
      matches.push({
        line: i + 2,
        item,
        label: [sku, name].filter(Boolean).join(" · ") || `row ${i + 2}`,
        onHand,
        ...(cols.bin >= 0 ? { bin: (r[cols.bin] ?? "").trim() } : {}),
        ...(cols.reorder >= 0 ? { reorderLevel: num(r[cols.reorder]) } : {}),
      });
    });
  }
  // The same item twice: the last row wins.
  const byItem = new Map(matches.filter((m) => m.item).map((m) => [m.item!.id, m]));
  const matched = [...byItem.values()];
  const unmatched = matches.filter((m) => !m.item);
  const changes = matched.filter((m) => !m.item!.trackInventory || (m.item!.onHandQty ?? 0) !== m.onHand);

  async function apply() {
    setBusy(true);
    setError("");
    try {
      const res = await call<{ changed: number; unchanged: number; missing: number }>(
        "importStockCounts",
        {
          tenantId,
          rows: matched.map((m) => ({ itemId: m.item!.id, onHand: m.onHand, ...(m.bin !== undefined ? { bin: m.bin } : {}), ...(m.reorderLevel !== undefined ? { reorderLevel: m.reorderLevel } : {}) })),
          note: `Stock count import (${fileName})`.slice(0, 200),
        },
        { timeoutMs: 300_000 },
      );
      onDone(`Stock counts imported: ${res.changed} changed, ${res.unchanged} already right${unmatched.length ? `, ${unmatched.length} rows didn't match an item` : ""}.`);
      setRows(null);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <CardTitle className="mb-0">Import stock counts</CardTitle>
          <p className="text-xs text-steel">From FieldPulse (Inventory → Export) or any spreadsheet saved as CSV: a part number or name, and the quantity on hand.</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border border-steel/30 bg-white px-3 py-1.5 text-sm text-ink hover:border-steel">
          <FileUp size={14} /> {rows ? "Pick another file" : "Pick a CSV file"}
          <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) void load(f); }} />
        </label>
      </div>
      {rows ? (
        <>
          <div className="grid gap-2 sm:grid-cols-5">
            {(Object.keys(LABEL) as Col[]).map((c) => (
              <div key={c}>
                <div className="text-[11px] text-steel">{LABEL[c]}</div>
                <Select className="py-1 text-xs" value={String(cols[c])} onChange={(e) => setCols((x) => ({ ...x, [c]: parseInt(e.target.value, 10) }))}>
                  <option value="-1">(none)</option>
                  {header.map((h, i) => (
                    <option key={i} value={String(i)}>
                      {h || `Column ${i + 1}`}
                    </option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          {cols.qty < 0 || (cols.sku < 0 && cols.name < 0) ? (
            <p className="text-xs text-brandorange-dark">Pick the quantity column and a part number or name column.</p>
          ) : (
            <>
              <p className="text-sm text-ink">
                {matched.length} item{matched.length === 1 ? "" : "s"} matched · <strong>{changes.length}</strong> will change
                {unmatched.length ? <span className="text-brandorange-dark"> · {unmatched.length} rows match no item</span> : null}
              </p>
              <div className="max-h-64 overflow-y-auto rounded border border-steel/15 text-xs">
                {changes.slice(0, 300).map((m) => (
                  <div key={m.item!.id} className="flex gap-2 border-b border-steel/10 px-2 py-1 last:border-0">
                    <span className="min-w-0 flex-1 truncate text-ink">{m.item!.name}</span>
                    <span className="text-steel">{m.item!.trackInventory ? `${m.item!.onHandQty ?? 0} →` : "not tracked →"}</span>
                    <span className="w-14 text-right font-semibold text-ink">{m.onHand}</span>
                  </div>
                ))}
                {unmatched.slice(0, 100).map((m) => (
                  <div key={`u${m.line}`} className={cn("flex gap-2 border-b border-steel/10 px-2 py-1 last:border-0 text-brandorange-dark")}>
                    <span className="min-w-0 flex-1 truncate">Row {m.line}: {m.label}</span>
                    <span>no matching item</span>
                  </div>
                ))}
              </div>
              {unmatched.length ? <p className="text-[11px] text-steel">Rows that match nothing are skipped. Add those items to the pricebook (same part number) and import again.</p> : null}
              <div className="flex gap-2">
                <Button busy={busy} disabled={matched.length === 0} onClick={() => void apply()}>
                  Set {matched.length} count{matched.length === 1 ? "" : "s"}
                </Button>
                <Button variant="ghost" onClick={() => setRows(null)}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          {error ? <p className="text-xs text-danger">{error}</p> : null}
        </>
      ) : null}
    </Card>
  );
}
